import React from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { useNavigate } from 'react-router-dom';
import { ServiceTypeCount } from '../../../types/utility.types';

interface ServiceTypeDistributionProps {
  data: ServiceTypeCount;
}

// Standardized color mapping for utility types
const UTILITY_TYPE_COLORS: Record<string, string> = {
  'electric': '#10B981', // Green
  'gas': '#F59E0B', // Orange
  'water': '#3B82F6', // Blue
  'telecoms': '#8B5CF6', // Purple
  'waste': '#EF4444', // Red
  'oil': '#6B7280', // Gray
  'export': '#06B6D4', // Cyan
  'renewable': '#84CC16', // Lime
  'insurance': '#EC4899', // Pink
  'broadband': '#7C3AED', // Violet
  'mobile': '#F97316', // Orange-red
  'landline': '#14B8A6', // Teal
  'default': '#64748B' // Slate gray for unknown types
};

const ServiceTypeDistribution: React.FC<ServiceTypeDistributionProps> = ({ data }) => {
  const navigate = useNavigate();

  // Define consistent ordering for service types
  const SERVICE_TYPE_ORDER = [
    'electric',
    'gas', 
    'water',
    'telecoms',
    'waste',
    'oil',
    'export',
    'renewable',
    'insurance',
    'broadband',
    'mobile',
    'landline'
  ];

  // Process data to ensure unique keys and normalize labels
  const processedData = Object.entries(data)
    .filter(([_, count]) => count > 0) // Filter out zero values
    .map(([type, count], index) => {
      const normalizedType = type.replace(/_/g, ' ').toLowerCase();
      const displayLabel = normalizedType
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      
      return {
        id: `${type}-${index}`, // Ensure unique IDs
        value: count,
        label: displayLabel,
        color: UTILITY_TYPE_COLORS[normalizedType] || UTILITY_TYPE_COLORS.default,
        normalizedType,
        sortOrder: SERVICE_TYPE_ORDER.indexOf(normalizedType)
      };
    })
    .sort((a, b) => {
      // Sort by predefined order, putting unknown types at the end
      const aOrder = a.sortOrder === -1 ? 999 : a.sortOrder;
      const bOrder = b.sortOrder === -1 ? 999 : b.sortOrder;
      return aOrder - bOrder;
    });

  // Handle click on pie chart segment
  const handleSegmentClick = (event: any, itemIdentifier: any) => {
    console.log('ServiceTypeDistribution click event:', event, itemIdentifier);
    if (itemIdentifier && itemIdentifier.dataIndex !== undefined) {
      const clickedItem = processedData[itemIdentifier.dataIndex];
      console.log('Clicked item:', clickedItem);
      if (clickedItem) {
        // Navigate to contracts page with service type filter
        const serviceTypeParam = clickedItem.label;
        console.log('Navigating to:', `/utilities/contracts?serviceType=${encodeURIComponent(serviceTypeParam)}`);
        navigate(`/utilities/contracts?serviceType=${encodeURIComponent(serviceTypeParam)}`);
      }
    }
  };

  return (
    <Card sx={{
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      backdropFilter: 'blur(4px)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: 2,
      color: 'white'
    }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
          Service Type Distribution
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
          Click on a legend item to view contracts
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', height: 300 }}>
          <Box sx={{ flex: 1, height: '100%' }}>
            <PieChart
              series={[
                {
                  data: processedData,
                  highlightScope: { faded: 'global', highlighted: 'item' },
                  faded: { innerRadius: 30, additionalRadius: -30 },
                },
              ]}
              height={300}
              margin={{ right: 20 }}
              legend={{ hidden: true }}
            />
          </Box>
          <Box sx={{ 
            width: 120, 
            ml: 2,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 1
          }}>
            {processedData.map((item, index) => (
              <Box
                key={item.id}
                onClick={() => {
                  console.log('Manual legend click:', item);
                  const serviceTypeParam = item.label;
                  console.log('Navigating to:', `/utilities/contracts?serviceType=${encodeURIComponent(serviceTypeParam)}`);
                  navigate(`/utilities/contracts?serviceType=${encodeURIComponent(serviceTypeParam)}`);
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  padding: '2px 4px',
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    backgroundColor: item.color,
                    borderRadius: '2px',
                    mr: 1,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body2" sx={{ 
                  fontSize: 12, 
                  color: 'white',
                  lineHeight: 1.2,
                  fontWeight: 400
                }}>
                  {item.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export { ServiceTypeDistribution };
